<script setup lang="ts">
import {
    Button,
    PROVIDER_MARK_NAMES,
    type ProviderMarkName,
    ZhBanner,
    ZhCheckRow,
    ZhPanel,
    ZhProviderMark,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { MemberToolGap } from '@/api'
import { useUiMode } from '@/composables/useUiMode'
import type { HolderToolGapsEntry } from '@/lib/holderToolGaps'
import { toolLabel } from '@/lib/presentation'

/**
 * #637: the tools a member's roles' tasks need that no connection of theirs reaches, read from the API
 * (`api.memberToolGaps`), with Connect to that member's Tools for each vendor. One component for the member page and
 * the screens a role holder is added on, so they cannot disagree.
 *
 * Every surface and control is the kit's, as CTO decision 9 names them: a `ZhPanel` under a `ZhSectionHeading`, one
 * `ZhCheckRow` per missing vendor (its kit status reads Missing) with the vendor's `ZhProviderMark` where the kit has
 * one, and the kit `Button`. On the add-holder screens (`banner`) each member's list is headed by the kit's
 * `ZhBanner`: the banner is the kit's one-line strip, so it carries the one-line summary and the rows sit under it.
 *
 * Standard prints the vendor's display name and the roles' and tasks' names; Advanced adds the exact tool names and
 * prints slugs. While a member's read is out the list says it is checking; a refused read says so, with Try again,
 * so an empty page never reads as "all connected". The list itself shows only when something is missing.
 */
const props = defineProps<{
    /** One entry per member the list is for, each with what their roles' tasks still need. */
    entries: HolderToolGapsEntry[]
    /** The section's test id; each row's is `${rowTestid}-<vendor>`. */
    sectionTestid: string
    rowTestid: string
    /** The add-holder screens: each member's list is headed by the kit's `ZhBanner` line. */
    banner?: boolean
}>()

const emit = defineEmits<{
    /** Read this member's list again, after a refused read. */
    retry: [memberId: string]
}>()

const router = useRouter()
const { mode } = useUiMode()

const checking = computed(() => props.entries.filter(entry => entry.state === 'checking'))
const failed = computed(() => props.entries.filter(entry => entry.state === 'failed'))
const shown = computed(() => props.entries.filter(entry => !entry.state && entry.gaps.length > 0))

function vendorName(vendor: string): string {
    return toolLabel(vendor, mode.value)
}

function vendors(gaps: MemberToolGap[]): string {
    return gaps.map(gap => vendorName(gap.vendor)).join(', ')
}

/** Standard reads names; Advanced reads the slugs, as the API stores them. */
function roleWords(gap: MemberToolGap): string[] {
    return mode.value === 'advanced' ? gap.roles : (gap.roleNames ?? gap.roles)
}

function taskWords(gap: MemberToolGap): string[] {
    return mode.value === 'advanced' ? gap.tasks : (gap.taskNames ?? gap.tasks)
}

function roles(gaps: MemberToolGap[]): string {
    return [...new Set(gaps.flatMap(roleWords))].join(', ')
}

/** The kit draws marks for a few providers only; any other vendor goes by its name alone. */
function markOf(vendor: string): ProviderMarkName | undefined {
    return (PROVIDER_MARK_NAMES as readonly string[]).includes(vendor) ? (vendor as ProviderMarkName) : undefined
}

/** The add-holder screens' one line: whose runs cannot start, and what is not connected for them. */
function bannerLine(entry: HolderToolGapsEntry): string {
    const one = entry.gaps.length === 1
    return `${entry.memberName}’s runs can’t start until ${vendors(entry.gaps)} ${one ? 'is' : 'are'} connected for them.`
}

/** The member's Tools page, opened on connecting this vendor for them. */
async function connect(memberId: string, vendor: string): Promise<void> {
    await router.push({ path: '/tools', query: { connect: vendor, member: memberId } })
}
</script>

<template>
	<div v-if="checking.length || failed.length || shown.length" class="space-y-3">
		<p
			v-for="entry in checking"
			:key="`checking-${entry.memberId}`"
			class="text-muted-foreground text-sm"
			role="status"
			:data-testid="`${sectionTestid}-checking`"
		>
			Checking {{ entry.memberName }}’s tools…
		</p>
		<p
			v-for="entry in failed"
			:key="`failed-${entry.memberId}`"
			class="text-muted-foreground text-sm"
			role="status"
			:data-testid="`${sectionTestid}-failed`"
		>
			Couldn’t check {{ entry.memberName }}’s tools.
			<Button type="button" variant="link" size="sm" class="h-auto px-0" @click="emit('retry', entry.memberId)">
				Try again
			</Button>
		</p>
		<ZhPanel v-if="shown.length" class="space-y-3" :data-testid="sectionTestid">
			<ZhSectionHeading>Tools to connect for their roles</ZhSectionHeading>
			<div v-for="entry in shown" :key="entry.memberId" class="space-y-2">
				<ZhBanner v-if="banner" :full-text="bannerLine(entry)" :data-testid="`${sectionTestid}-banner`">
					{{ bannerLine(entry) }}
				</ZhBanner>
				<p class="text-muted-foreground text-sm">
					{{ entry.memberName }} holds {{ roles(entry.gaps) }}. Those tasks need {{ vendors(entry.gaps) }}, which
					{{ entry.gaps.length === 1 ? 'is' : 'are' }} not connected for them yet. Connect opens Tools to connect
					{{ entry.gaps.length === 1 ? 'it' : 'each one' }} for {{ entry.memberName }}.
				</p>
				<table class="w-full text-left text-sm">
					<caption class="sr-only">Tools not connected for {{ entry.memberName }}</caption>
					<tbody>
						<ZhCheckRow
							v-for="gap in entry.gaps"
							:key="gap.vendor"
							state="failed"
							:data-testid="`${rowTestid}-${gap.vendor}`"
						>
							<td class="py-2">
								<span class="flex items-center gap-2 font-medium">
									<ZhProviderMark v-if="markOf(gap.vendor)" :provider="markOf(gap.vendor) as ProviderMarkName" />
									{{ vendorName(gap.vendor) }}
								</span>
								<span class="text-muted-foreground block">
									Needed by {{ taskWords(gap).join(', ') }} ({{ roleWords(gap).join(', ') }})
								</span>
								<span v-if="mode === 'advanced'" class="text-muted-foreground block font-mono text-xs">
									{{ gap.tools.join(', ') }}
								</span>
							</td>
							<td class="py-2 text-right">
								<Button type="button" size="sm" variant="outline" @click="connect(entry.memberId, gap.vendor)">
									Connect
								</Button>
							</td>
						</ZhCheckRow>
					</tbody>
				</table>
			</div>
		</ZhPanel>
	</div>
</template>
